'use client';

import useMarketSelectorDropdown from './hooks/useMarketSelectorDropdown';
import MarketsSearchBar from './MarketsSearchBar';
import MarketCategoryTabs from './MarketCategoryTabs';
import { MarketType } from '@velocity-exchange/sdk';
import PopoverWrapper, { FloatingPopoverProps } from '../PopoverWrapper';
import BottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/BottomDrawer';
import { cj } from 'src/utils/classNames';
import MarketSelectorTable from 'src/components/MarketSelector/MarketSelectorTable/MarketSelectorTable';
import { memo } from 'react';

interface Props extends FloatingPopoverProps {
	onClose?: () => void;
	marketType: MarketType;
	closeDrawer: () => void;
	isDrawerClosing: boolean;
}

/**
 * Contains the search bar, market category tabs, and table of markets.
 *
 * Note, on screen sizes < sm, this is rendered within a bottom drawer.
 */
const MarketSelectorDropdown = ({
	onClose,
	marketType,
	setFloating,
	getFloatingProps,
	floatingStyles,
	closeDrawer,
	isDrawerClosing,
}: Props) => {
	const {
		displayedMarkets,
		searchValue,
		setSearchValue,
		selectedCategory,
		setSelectedCategory,
		sort,
		toggleSort,
		isMobileScreenSize,
		isFavoritesSticky,
		toggleIsFavoritesSticky,
	} = useMarketSelectorDropdown();

	const content = (
		<div
			className={cj(
				'relative flex flex-col rounded-xs bg-container-bg',
				isMobileScreenSize && 'min-h-[calc(90vh-2rem)]'
			)}
		>
			<div className="flex flex-col gap-y-md border-b border-stroke-divider p-md pb-md">
				<MarketsSearchBar
					id="market-selector-search"
					searchValue={searchValue}
					setSearchValue={setSearchValue}
					placeholder="Search Markets"
				/>
				<MarketCategoryTabs
					selectedCategory={selectedCategory}
					setSelectedCategory={setSelectedCategory}
				/>
			</div>
			<MarketSelectorTable
				markets={displayedMarkets}
				marketType={marketType}
				onClose={isMobileScreenSize ? closeDrawer : onClose}
				toggleSort={toggleSort}
				sort={sort}
				marketCategory={selectedCategory}
				isFavoritesSticky={isFavoritesSticky}
				toggleIsFavoritesSticky={toggleIsFavoritesSticky}
			/>
		</div>
	);

	if (isMobileScreenSize) {
		return (
			<BottomDrawer.Container
				closeDrawer={closeDrawer}
				isDrawerClosing={isDrawerClosing}
			>
				<BottomDrawer.Title closeDrawer={closeDrawer}>
					Markets
				</BottomDrawer.Title>
				<BottomDrawer.Body
					// `overflow-y-hidden` because we only want the nested table scrollable,
					// not the entire body.
					className="overflow-y-hidden p-0"
					asChild
				>
					{content}
				</BottomDrawer.Body>
			</BottomDrawer.Container>
		);
	}

	return (
		<PopoverWrapper
			ref={setFloating}
			style={floatingStyles}
			{...getFloatingProps()}
			className="max-w-full shadow-md sm:w-[712px]"
		>
			{content}
		</PopoverWrapper>
	);
};

export default memo(MarketSelectorDropdown);
